import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { jobsAPI, companiesAPI, jobCategoriesAPI } from '../services/api';
import { Job, Company, JobFilters, JobCategory } from '../types';
import JobCard from '../components/JobCard';
import { useAuth } from '../contexts/AuthContext';
import { Card, Button, EmptyState, SectionHeader } from '../components/ui';
import { Icon } from '../components/ui/Icon';
import { fadeInUp, stagger } from '../utils/motion';
import { SEO } from '../components/seo/SEO';
import { buildJobListJsonLd } from '../utils/jsonLd';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const Jobs: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryFromUrl = searchParams.get('q') || '';
  const { isAuthenticated, isCandidate, user } = useAuth();

  const [jobs, setJobs] = React.useState<Job[]>([]);
  const [companies, setCompanies] = React.useState<Company[]>([]);
  const [companyQuery, setCompanyQuery] = React.useState('');
  const [showCompanySuggestions, setShowCompanySuggestions] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  const [pagination, setPagination] = React.useState({
    current_page: 1,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false,
  });

  const [filters, setFilters] = React.useState<JobFilters>({
    search: queryFromUrl,
    location: '',
    job_type: '',
    candidate_total_months: undefined,
    remote_allowed: undefined,
    company_id: undefined,
    page: 1,
    per_page: 10,
    category: searchParams.get('category') || undefined,
  });

  const [savedJobIds, setSavedJobIds] = React.useState<Set<number>>(new Set());
  const [savingJobId, setSavingJobId] = React.useState<number | null>(null);
  const [categoryTree, setCategoryTree] = React.useState<JobCategory[]>([]);
  const [categoryParentId, setCategoryParentId] = React.useState<number | null>(null);

  const fetchJobs = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await jobsAPI.getJobs(filters);
      setJobs(response.jobs);
      setPagination({
        current_page: response.current_page,
        total: response.total,
        pages: response.pages,
        has_next: response.has_next,
        has_prev: response.has_prev,
      });
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  React.useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  React.useEffect(() => {
    const nextQuery = (searchParams.get('q') || '').trim();
    setFilters((prev) => {
      if ((prev.search || '').trim() === nextQuery) return prev;
      return { ...prev, search: nextQuery, page: 1 };
    });
  }, [searchParams]);

  // Load category tree on mount
  React.useEffect(() => {
    jobCategoriesAPI.getTree()
      .then((res) => setCategoryTree(res.categories || []))
      .catch(() => setCategoryTree([]));
  }, []);

  // Pre-select parent when the URL cold-loads with ?category=<slug>
  React.useEffect(() => {
    if (!filters.category || categoryTree.length === 0) return;
    for (const parent of categoryTree) {
      const match = (parent.children || []).find((c) => c.slug === filters.category);
      if (match) {
        setCategoryParentId(parent.id);
        return;
      }
    }
  }, [filters.category, categoryTree]);

  React.useEffect(() => {
    const loadSavedJobs = async () => {
      if (!isAuthenticated || !isCandidate) {
        setSavedJobIds(new Set());
        return;
      }
      try {
        let page = 1;
        let hasNext = true;
        const ids = new Set<number>();
        while (hasNext && page <= 20) {
          const data = await jobsAPI.getSavedJobs({ page, per_page: 100 });
          (data.saved_jobs || []).forEach((item) => ids.add(item.job_id));
          hasNext = data.has_next;
          page += 1;
        }
        setSavedJobIds(ids);
      } catch (error) {
        console.error('Failed to load saved jobs:', error);
      }
    };
    loadSavedJobs();
  }, [isAuthenticated, isCandidate]);

  React.useEffect(() => {
    const fetchCompanies = async () => {
      if (!isAuthenticated) {
        setCompanies([]);
        return;
      }
      try {
        const response = await companiesAPI.getCompanies({ per_page: 100 });
        setCompanies(response.companies);
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      }
    };
    fetchCompanies();
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (!filters.company_id) {
      setCompanyQuery('');
      return;
    }
    const selectedCompany = companies.find((c) => c.id === Number(filters.company_id));
    if (selectedCompany) setCompanyQuery(selectedCompany.name);
  }, [filters.company_id, companies]);

  // Prefill the candidate experience filter from the logged-in user's profile
  // (years + months) the first time we render. Only sets on initial mount per
  // user; the user can clear or override after.
  const candidatePrefilledRef = React.useRef(false);
  React.useEffect(() => {
    if (candidatePrefilledRef.current) return;
    if (!isAuthenticated || !isCandidate || !user) return;
    const y = Number((user as any).experience_years) || 0;
    const m = Number((user as any).experience_months) || 0;
    const total = y * 12 + m;
    if (total > 0) {
      setFilters((prev) => prev.candidate_total_months === undefined ? { ...prev, candidate_total_months: total } : prev);
    }
    candidatePrefilledRef.current = true;
  }, [isAuthenticated, isCandidate, user]);

  const handleFilterChange = (key: keyof JobFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (size: number) => {
    setFilters((prev) => ({ ...prev, per_page: size, page: 1 }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      location: '',
      job_type: '',
      candidate_total_months: undefined,
      remote_allowed: undefined,
      company_id: undefined,
      page: 1,
      per_page: filters.per_page || 10,
      category: undefined,
    });
    setCompanyQuery('');
    setShowCompanySuggestions(false);
    setCategoryParentId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('category');
      return next;
    });
  };

  const handleToggleSave = async (jobId: number, shouldSave: boolean) => {
    if (!isAuthenticated || !isCandidate) return;
    try {
      setSavingJobId(jobId);
      if (shouldSave) {
        await jobsAPI.saveJob(jobId);
        setSavedJobIds((prev) => new Set(prev).add(jobId));
      } else {
        await jobsAPI.unsaveJob(jobId);
        setSavedJobIds((prev) => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
      }
    } catch (error) {
      console.error('Failed to update saved job:', error);
    } finally {
      setSavingJobId(null);
    }
  };

  const companySuggestions = React.useMemo(() => {
    const query = companyQuery.trim().toLowerCase();
    if (!query) return companies.slice(0, 8);
    return companies
      .filter((c) => c.name.toLowerCase().includes(query))
      .slice(0, 8);
  }, [companies, companyQuery]);

  const selectCompany = (company: Company) => {
    setCompanyQuery(company.name);
    setShowCompanySuggestions(false);
    handleFilterChange('company_id', company.id);
  };

  // Compute canonical URL when a category child filter is active
  const canonicalUrl = React.useMemo(() => {
    if (!filters.category || categoryTree.length === 0) return undefined;
    for (const parent of categoryTree) {
      const match = (parent.children || []).find((c) => c.slug === filters.category);
      if (match) return `/jobs/category/${parent.slug}/${match.slug}`;
    }
    return undefined;
  }, [filters.category, categoryTree]);

  const renderPagination = () => {
    if (pagination.pages <= 1) return null;
    const startPage = Math.max(1, pagination.current_page - 2);
    const endPage = Math.min(pagination.pages, pagination.current_page + 2);
    const pages: number[] = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    return (
      <div className="jobs-v3__pager">
        <Button
          variant="secondary"
          size="sm"
          disabled={!pagination.has_prev}
          onClick={() => handlePageChange(pagination.current_page - 1)}
        >Previous</Button>
        <div className="jobs-v3__pager-pages">
          {pages.map((p) => (
            <button
              key={p}
              type="button"
              className={`jobs-v3__pager-num ${p === pagination.current_page ? 'is-active' : ''}`}
              onClick={() => handlePageChange(p)}
            >{p}</button>
          ))}
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={!pagination.has_next}
          onClick={() => handlePageChange(pagination.current_page + 1)}
        >Next</Button>
      </div>
    );
  };

  const hasActiveFilters = Boolean(
    filters.search || filters.location || filters.job_type ||
    filters.candidate_total_months !== undefined || filters.remote_allowed !== undefined || filters.company_id ||
    filters.category
  );

  return (
    <div className="container portal-v3-page">
      <SEO
        title="Browse Open Jobs"
        description="Discover thousands of open roles from top companies. AI-ranked matches across engineering, data, product, and more on talixjobs."
        jsonLd={jobs.length > 0 ? buildJobListJsonLd(jobs) : undefined}
        canonicalUrl={canonicalUrl}
      />
      <SectionHeader
        eyebrow="Browse jobs"
        title="Find your next opportunity"
        description="AI-ranked roles from across our partner companies."
      />

      <Card padded className="jobs-v3__filters">
        <div className="jobs-v3__filter-row">
          <div className="jobs-v3__field jobs-v3__field--grow">
            <label>Search</label>
            <div className="jobs-v3__search-box">
              <Icon name="search" size={16} />
              <input
                type="text"
                placeholder="Title, skills, keywords"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
          </div>
          <div className="jobs-v3__field">
            <label>Location</label>
            <input
              type="text"
              placeholder="City or country"
              value={filters.location || ''}
              onChange={(e) => handleFilterChange('location', e.target.value)}
            />
          </div>
          <div className="jobs-v3__field">
            <label>Job type</label>
            <select
              value={filters.job_type || ''}
              onChange={(e) => handleFilterChange('job_type', e.target.value)}
            >
              <option value="">All</option>
              <option value="full-time">Full time</option>
              <option value="part-time">Part time</option>
              <option value="contract">Contract</option>
              <option value="internship">Internship</option>
            </select>
          </div>
          <div className="jobs-v3__field">
            <label>Your experience (months)</label>
            <input
              type="number"
              min={0}
              max={600}
              placeholder="e.g. 36"
              value={filters.candidate_total_months ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  handleFilterChange('candidate_total_months', undefined);
                  return;
                }
                const n = parseInt(raw, 10);
                handleFilterChange('candidate_total_months', Number.isFinite(n) && n >= 0 ? n : undefined);
              }}
            />
          </div>
        </div>

        <div className="jobs-v3__filter-row">
          {isAuthenticated && (
            <div className="jobs-v3__field jobs-v3__field--grow">
              <label>Company</label>
              <div className="jobs-v3__autocomplete">
                <input
                  type="text"
                  placeholder="Type a company name"
                  value={companyQuery}
                  onFocus={() => setShowCompanySuggestions(true)}
                  onBlur={() => window.setTimeout(() => setShowCompanySuggestions(false), 150)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCompanyQuery(value);
                    setShowCompanySuggestions(true);
                    if (!value.trim()) {
                      handleFilterChange('company_id', undefined);
                    }
                  }}
                />
                {showCompanySuggestions && companySuggestions.length > 0 && (
                  <div className="jobs-v3__autocomplete-list">
                    {companySuggestions.map((company) => (
                      <button
                        key={company.id}
                        type="button"
                        className="jobs-v3__autocomplete-item"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          selectCompany(company);
                        }}
                      >
                        {company.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="jobs-v3__field">
            <label>Workplace</label>
            <select
              value={filters.remote_allowed === undefined ? '' : filters.remote_allowed.toString()}
              onChange={(e) =>
                handleFilterChange('remote_allowed', e.target.value === '' ? undefined : e.target.value === 'true')
              }
            >
              <option value="">All</option>
              <option value="true">Remote only</option>
              <option value="false">On-site only</option>
            </select>
          </div>
          <div className="jobs-v3__field jobs-v3__field--actions">
            <Button
              variant="ghost"
              size="md"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
            >
              Clear filters
            </Button>
          </div>
        </div>

        <div className="jobs-v3__filter-row">
          <div className="jobs-v3__field jobs-v3__field--grow">
            <label htmlFor="categoryParent">Category</label>
            <select
              id="categoryParent"
              value={categoryParentId ?? ''}
              onChange={(e) => {
                const nextParent = e.target.value ? Number(e.target.value) : null;
                setCategoryParentId(nextParent);
                // Clear the child filter when parent changes
                setFilters((prev) => ({ ...prev, category: undefined, page: 1 }));
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('category');
                  return next;
                });
              }}
            >
              <option value="">All categories</option>
              {categoryTree.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="jobs-v3__field jobs-v3__field--grow">
            <label htmlFor="categoryChild">Role</label>
            <select
              id="categoryChild"
              value={filters.category ?? ''}
              disabled={categoryParentId === null}
              onChange={(e) => {
                const nextSlug = e.target.value || undefined;
                setFilters((prev) => ({ ...prev, category: nextSlug, page: 1 }));
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  if (nextSlug) next.set('category', nextSlug);
                  else next.delete('category');
                  return next;
                });
              }}
            >
              <option value="">
                {categoryParentId === null ? 'Pick a category first' : 'All roles'}
              </option>
              {(categoryTree.find((p) => p.id === categoryParentId)?.children || []).map((c) => (
                <option key={c.id} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card padded>
          <div style={{ textAlign: 'center', padding: 'var(--s-6)', color: '#64748b' }}>
            Searching for jobs...
          </div>
        </Card>
      ) : (
        <>
          <div className="jobs-v3__summary">
            <span className="jobs-v3__summary-count">
              {pagination.total === 0
                ? 'No jobs found'
                : `Showing ${(pagination.current_page - 1) * (filters.per_page || 10) + 1} – ${Math.min(pagination.current_page * (filters.per_page || 10), pagination.total)} of ${pagination.total}`
              }
            </span>
            <div className="jobs-v3__summary-pp">
              <span>Rows per page</span>
              <select
                value={filters.per_page || 10}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>

          {jobs.length > 0 ? (
            <>
              <motion.div
                variants={stagger(0.04)}
                initial="hidden"
                animate="visible"
                className="jobs-v3__results"
              >
                {jobs.map((job) => (
                  <motion.div key={job.id} variants={fadeInUp}>
                    <JobCard
                      job={job}
                      showSaveAction={isAuthenticated && isCandidate}
                      isSaved={savedJobIds.has(job.id)}
                      saveLoading={savingJobId === job.id}
                      onToggleSave={handleToggleSave}
                    />
                  </motion.div>
                ))}
              </motion.div>
              {renderPagination()}
            </>
          ) : (
            <EmptyState
              title="No jobs match your filters"
              description="Try a broader search or clear active filters."
              icon={<Icon name="search" size={28} />}
              action={
                <Button variant="secondary" onClick={clearFilters}>Clear all filters</Button>
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export default Jobs;
